import { Quote } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/common/logo";
import { Starfield } from "@/components/common/starfield";
import { ColorSchemeToggle } from "@/components/layout/color-scheme-toggle";
import {
  DiagnosticsBadge,
  DiagnosticsSheet,
  useGlobalErrorCapture,
} from "@/components/layout/diagnostics";

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  wide = false,
}: {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  // A failed sign-in is the one error people can least afford to be unable to
  // read, and this side of the app has no shell to hang the panel off.
  const [diagnosticsOpen, setDiagnosticsOpen] = React.useState(false);
  useGlobalErrorCapture();

  return (
    <div className="relative min-h-dvh lg:grid lg:grid-cols-[1fr_minmax(0,44rem)]">
      {/* Brand panel — hidden on mobile, where it would push the form below the fold */}
      <aside className="relative hidden overflow-hidden border-r bg-card lg:flex lg:flex-col lg:justify-between lg:p-10">
        <Starfield />
        <div className="relative">
          <Link to="/" className="inline-flex rounded-lg focus-visible:ring-[3px] focus-visible:ring-ring/30">
            <Logo />
          </Link>
        </div>

        <div className="relative max-w-md space-y-6">
          <h2 className="text-3xl font-semibold tracking-tight text-balance">
            Know what your business is worth. Then grow it on purpose.
          </h2>
          <p className="text-muted-foreground">
            Twelve figures a month is all it takes. Rocketeer Orbit turns them into a valuation, a
            growth score, and the one task that matters next.
          </p>
          <figure className="rounded-xl border bg-background/40 p-4 backdrop-blur-sm">
            <Quote className="size-4 text-brand" />
            <blockquote className="mt-2 text-sm text-pretty">
              “We went from guessing to knowing. The valuation range gave us the confidence to turn
              down our first offer — and take a better one nine months later.”
            </blockquote>
            <figcaption className="mt-3 text-xs text-muted-foreground">
              Founder, Fitness &amp; Leisure · Navigator plan
            </figcaption>
          </figure>
        </div>

        <p className="relative text-xs text-muted-foreground">
          © {new Date().getFullYear()} Rocketeer Orbit. All rights reserved.
        </p>
      </aside>

      {/* Form panel */}
      <main className="relative flex min-h-dvh flex-col">
        <div className="flex items-center justify-between p-5 lg:justify-end">
          <Link to="/" className="lg:hidden">
            <Logo />
          </Link>
          <div className="flex items-center gap-1">
            <DiagnosticsBadge onClick={() => setDiagnosticsOpen(true)} />
            <ColorSchemeToggle />
          </div>
        </div>
        <DiagnosticsSheet open={diagnosticsOpen} onOpenChange={setDiagnosticsOpen} />

        <div className="flex flex-1 items-start justify-center px-5 pb-12 sm:items-center">
          <div className={wide ? "w-full max-w-xl" : "w-full max-w-sm"}>
            <div className="animate-rise space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
              {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            <div className="mt-7 animate-rise [animation-delay:60ms]">{children}</div>
            {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
          </div>
        </div>
      </main>
    </div>
  );
}
